import React from 'react';
import {
    Typography,
    Card,
    CardContent,
    Box,
    Input,
    Button,
    Stack,
    FormControl,
    FormLabel,
    Alert,
    Table,
    Chip,
    Modal,
    ModalDialog,
    CircularProgress,
    Divider,
    Select,
    Option,
    Checkbox,
    Sheet,
    Grid,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    AspectRatio,
    Badge
} from '@mui/joy';
import MainLayout from '../../components/Layout/MainLayout';
import api from '../../utils/api';

const DeviceGroupsPage = () => {
    // Groups state
    const [groups, setGroups] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');

    // Create group modal state
    const [openCreateGroup, setOpenCreateGroup] = React.useState(false);
    const [groupForm, setGroupForm] = React.useState({
        group_name: '',
        description: '',
        location: '',
        group_type: 'custom'
    });
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState('');

    // Add devices modal state
    const [openAddDevices, setOpenAddDevices] = React.useState(false);
    const [selectedGroup, setSelectedGroup] = React.useState(null);
    const [availableDevices, setAvailableDevices] = React.useState([]);
    const [selectedDeviceIds, setSelectedDeviceIds] = React.useState([]);

    // Assign content modal state
    const [openAssignContent, setOpenAssignContent] = React.useState(false);
    const [contentForm, setContentForm] = React.useState({
        assignment_type: 'layout',
        layout_id: '',
        priority: 1
    });
    const [layouts, setLayouts] = React.useState([]);
    const [content, setContent] = React.useState([]);
    const [forceUpdate, setForceUpdate] = React.useState(0);

    // Enhanced content selection
    const [selectedContentIds, setSelectedContentIds] = React.useState([]);
    const [activeContentTab, setActiveContentTab] = React.useState('all');

    // Fetch device groups
    const fetchGroups = async () => {
        try {
            setLoading(true);
            setError('');
            const { data } = await api.get('/api/device-groups');
            if (data?.success) {
                setGroups(data.groups || []);
            } else {
                setError(data?.message || 'Failed to load device groups');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to load device groups';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    // Fetch available devices for adding to groups
    const fetchAvailableDevices = async () => {
        try {
            const { data } = await api.get('/api/devices');
            if (data?.success) {
                setAvailableDevices(data.devices || []);
            }
        } catch (err) {
            console.error('Failed to fetch devices:', err);
        }
    };

    // Fetch only layouts and content (no playlists/schedules)
    const fetchContentOptions = async () => {
        try {
            console.log('🔄 Starting fetchContentOptions (layouts + content only)...');

            // Only call layouts and content APIs
            console.log('📡 Calling /api/layouts...');
            const layoutsRes = await api.get('/api/layouts');
            console.log('📡 Layouts response received:', layoutsRes.status, layoutsRes.data);

            console.log('📡 Calling /api/content...');
            const contentRes = await api.get('/api/content');
            console.log('📡 Content response received:', contentRes.status);

            // Process layouts
            if (layoutsRes.data?.success && layoutsRes.data.layouts) {
                const layoutsData = layoutsRes.data.layouts;
                console.log('✅ Processing layouts:', layoutsData.length, 'layouts found');
                console.log('✅ Layout details:', layoutsData.map(l => ({
                    id: l.id,
                    name: l.template_name,
                    hasConfig: !!l.layout_config
                })));

                // Force state update
                setLayouts([...layoutsData]);
                setForceUpdate(prev => prev + 1);
                console.log('✅ Layouts state updated, force counter incremented');
            } else {
                console.error('❌ Layouts API failed or no layouts:', layoutsRes.data);
                setLayouts([]);
            }

            // Process content
            if (contentRes.data?.success) {
                setContent(contentRes.data.content || []);
                console.log('✅ Content loaded:', contentRes.data.content?.length || 0, 'items');
            }

            console.log('🎉 fetchContentOptions completed successfully');
        } catch (err) {
            console.error('💥 fetchContentOptions error:', err);
            console.error('💥 Error details:', err.response?.data || err.message);
            setLayouts([]);
            setForceUpdate(prev => prev + 1); // Increment even on error to show debugging
        }
    };

    React.useEffect(() => {
        console.log('🚀 DeviceGroupsPage mounted - loading initial data...');
        fetchGroups();

        // Test if fetchContentOptions is working
        console.log('🧪 Testing fetchContentOptions...');
        fetchContentOptions().then(() => {
            console.log('✅ fetchContentOptions completed on page load');
        }).catch(error => {
            console.error('❌ fetchContentOptions failed on page load:', error);
        });
    }, []);

    // Create new device group
    const handleCreateGroup = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            setSubmitError('');

            const { data } = await api.post('/api/device-groups', groupForm);

            if (data?.success) {
                setOpenCreateGroup(false);
                setGroupForm({ group_name: '', description: '', location: '', group_type: 'custom' });
                fetchGroups(); // Refresh list
            } else {
                setSubmitError(data?.message || 'Failed to create group');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to create group';
            setSubmitError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // Add devices to group
    const handleAddDevices = async () => {
        if (!selectedGroup || selectedDeviceIds.length === 0) return;

        try {
            setSubmitting(true);
            const { data } = await api.post(`/api/device-groups/${selectedGroup.id}/devices`, {
                device_ids: selectedDeviceIds
            });

            if (data?.success) {
                setOpenAddDevices(false);
                setSelectedDeviceIds([]);
                fetchGroups(); // Refresh list
            } else {
                setSubmitError(data?.message || 'Failed to add devices to group');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to add devices to group';
            setSubmitError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // Assign content to group
    const handleAssignContent = async () => {
        if (!selectedGroup) return;

        try {
            setSubmitting(true);
            setSubmitError('');

            let assignmentData = {
                assignment_type: contentForm.assignment_type,
                priority: contentForm.priority
            };

            // Add specific data based on assignment type
            if (contentForm.assignment_type === 'layout') {
                assignmentData.layout_id = contentForm.layout_id;
            } else if (contentForm.assignment_type === 'content') {
                assignmentData.content_ids = selectedContentIds;
            }

            const { data } = await api.post(`/api/device-groups/${selectedGroup.id}/assignments`, assignmentData);

            if (data?.success) {
                setOpenAssignContent(false);
                setContentForm({
                    assignment_type: 'layout',
                    layout_id: '',
                    priority: 1
                });
                setSelectedContentIds([]);
                fetchGroups(); // Refresh list
            } else {
                setSubmitError(data?.message || 'Failed to assign content to group');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to assign content to group';
            setSubmitError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const openAddDevicesModal = (group) => {
        setSelectedGroup(group);
        setOpenAddDevices(true);
        fetchAvailableDevices();
    };

    const openAssignContentModal = (group) => {
        console.log('Opening assign content modal for group:', group.group_name);
        console.log('Current layouts in state:', layouts.length, layouts.map(l => ({ id: l.id, name: l.template_name })));

        setSelectedGroup(group);
        setOpenAssignContent(true);
        setSelectedContentIds([]);
        setActiveContentTab('all');

        // Data should already be loaded from page mount - NO FETCHING ON CLICK
        console.log('✅ Using pre-loaded data - no fetch on click');
    };

    // Get content statistics for tabs
    const getContentStats = () => {
        const stats = {
            total: content.length,
            image: content.filter(c => (c.type_name || '').toLowerCase() === 'image').length,
            video: content.filter(c => (c.type_name || '').toLowerCase() === 'video').length,
            document: content.filter(c => ['pdf', 'document'].includes((c.type_name || '').toLowerCase())).length,
            web: content.filter(c => (c.type_name || '').toLowerCase() === 'web').length
        };
        return stats;
    };

    // Filter content by category
    const getFilteredContent = () => {
        if (activeContentTab === 'all') {
            return content;
        }
        return content.filter(item => {
            const type = (item.type_name || '').toLowerCase();
            if (activeContentTab === 'document') {
                return ['pdf', 'document'].includes(type);
            }
            return type === activeContentTab;
        });
    };

    // Toggle content selection
    const toggleContentSelection = (contentId) => {
        setSelectedContentIds(prev => {
            if (prev.includes(contentId)) {
                return prev.filter(id => id !== contentId);
            } else {
                return [...prev, contentId];
            }
        });
    };

    // Build thumbnail URL
    const buildThumbnailUrl = (item) => {
        if (item.thumbnail_path) {
            return `${process.env.NODE_ENV === 'production' ? 'https://tv.vectorindia.org' : 'http://localhost:6011'}/${item.thumbnail_path}`;
        } else if ((item.type_name || '').toLowerCase() === 'image' && item.file_path) {
            return `${process.env.NODE_ENV === 'production' ? 'https://tv.vectorindia.org' : 'http://localhost:6011'}/${item.file_path}`;
        }
        return null;
    };

    // Get fallback thumbnail
    const getFallbackThumbnail = () => {
        return 'data:image/svg+xml;utf8,' + encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200">
                <rect width="100%" height="100%" fill="#f2f4f7"/>
                <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" 
                      font-family="Arial" font-size="16" fill="#98a2b3">Preview not available</text>
            </svg>`
        );
    };

    const getGroupTypeColor = (type) => {
        switch (type) {
            case 'location': return 'primary';
            case 'department': return 'success';
            case 'custom': return 'neutral';
            default: return 'neutral';
        }
    };

    const getStatusColor = (status) => {
        return status === 'active' ? 'success' : 'neutral';
    };

    return (
        <MainLayout currentPage="devices">
            <Box>
                <Typography level="h2" sx={{ fontWeight: 600, mb: 3 }}>
                    Device Groups Management
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Button onClick={() => setOpenCreateGroup(true)}>Create Group</Button>
                    <Button variant="outlined" onClick={() => fetchGroups()}>Refresh</Button>
                    <Box sx={{ ml: 'auto', color: 'neutral.600' }}>
                        {groups.length} groups
                    </Box>
                </Stack>

                <Card variant="outlined">
                    <CardContent>
                        {error ? (
                            <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        ) : null}

                        {loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                                <CircularProgress size="sm" />
                            </Box>
                        ) : (
                            <Table hoverRow>
                                <thead>
                                    <tr>
                                        <th>Group Name</th>
                                        <th>Type</th>
                                        <th>Location</th>
                                        <th>Devices</th>
                                        <th>Assignments</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groups.map((group) => (
                                        <tr key={group.id}>
                                            <td>
                                                <Box>
                                                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                                        {group.group_name}
                                                    </Typography>
                                                    {group.description && (
                                                        <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                                                            {group.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </td>
                                            <td>
                                                <Chip
                                                    size="sm"
                                                    color={getGroupTypeColor(group.group_type)}
                                                    variant="soft"
                                                >
                                                    {group.group_type}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">
                                                    {group.location || '-'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                                    {group.device_count || 0} devices
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">
                                                    {group.assignment_count || 0} assignments
                                                </Typography>
                                            </td>
                                            <td>
                                                <Chip
                                                    size="sm"
                                                    color={getStatusColor(group.status)}
                                                    variant="soft"
                                                >
                                                    {group.status}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Stack direction="row" spacing={1}>
                                                    <Button
                                                        size="sm"
                                                        variant="outlined"
                                                        onClick={() => openAddDevicesModal(group)}
                                                    >
                                                        Add Devices
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outlined"
                                                        onClick={() => openAssignContentModal(group)}
                                                    >
                                                        Assign Content
                                                    </Button>
                                                </Stack>
                                            </td>
                                        </tr>
                                    ))}
                                    {groups.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                                                <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                                                    No device groups found. Create your first group to get started.
                                                </Typography>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                {/* Create Group Modal */}
                <Modal open={openCreateGroup} onClose={() => setOpenCreateGroup(false)}>
                    <ModalDialog sx={{ minWidth: 400 }}>
                        <Typography level="h4" sx={{ mb: 2 }}>Create Device Group</Typography>
                        <form onSubmit={handleCreateGroup}>
                            <Stack spacing={2}>
                                {submitError && (
                                    <Alert color="danger" variant="soft">
                                        {submitError}
                                    </Alert>
                                )}
                                <FormControl required>
                                    <FormLabel>Group Name</FormLabel>
                                    <Input
                                        value={groupForm.group_name}
                                        onChange={(e) => setGroupForm(prev => ({ ...prev, group_name: e.target.value }))}
                                        placeholder="Lobby TVs"
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Description</FormLabel>
                                    <Input
                                        value={groupForm.description}
                                        onChange={(e) => setGroupForm(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="All lobby display screens"
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Location</FormLabel>
                                    <Input
                                        value={groupForm.location}
                                        onChange={(e) => setGroupForm(prev => ({ ...prev, location: e.target.value }))}
                                        placeholder="Main Lobby"
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Group Type</FormLabel>
                                    <Select
                                        value={groupForm.group_type}
                                        onChange={(e, value) => setGroupForm(prev => ({ ...prev, group_type: value }))}
                                    >
                                        <Option value="custom">Custom</Option>
                                        <Option value="location">Location-based</Option>
                                        <Option value="department">Department-based</Option>
                                    </Select>
                                </FormControl>
                                <Stack direction="row" spacing={1}>
                                    <Button type="submit" loading={submitting} disabled={submitting}>
                                        Create Group
                                    </Button>
                                    <Button variant="outlined" onClick={() => setOpenCreateGroup(false)}>
                                        Cancel
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                    </ModalDialog>
                </Modal>

                {/* Add Devices to Group Modal */}
                <Modal open={openAddDevices} onClose={() => setOpenAddDevices(false)}>
                    <ModalDialog sx={{ minWidth: 500 }}>
                        <Typography level="h4" sx={{ mb: 2 }}>
                            Add Devices to "{selectedGroup?.group_name}"
                        </Typography>
                        <Stack spacing={2}>
                            {submitError && (
                                <Alert color="danger" variant="soft">
                                    {submitError}
                                </Alert>
                            )}
                            <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                                Select devices to add to this group:
                            </Typography>
                            <Sheet variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', p: 2 }}>
                                <Stack spacing={1}>
                                    {availableDevices.map((device) => (
                                        <Box key={device.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Checkbox
                                                checked={selectedDeviceIds.includes(device.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedDeviceIds(prev => [...prev, device.id]);
                                                    } else {
                                                        setSelectedDeviceIds(prev => prev.filter(id => id !== device.id));
                                                    }
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                                    {device.device_name}
                                                </Typography>
                                                <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                                                    {device.location || 'No location'} • {device.status}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                size="sm"
                                                color={device.status === 'online' ? 'success' : 'neutral'}
                                                variant="soft"
                                            >
                                                {device.status}
                                            </Chip>
                                        </Box>
                                    ))}
                                </Stack>
                            </Sheet>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    onClick={handleAddDevices}
                                    loading={submitting}
                                    disabled={submitting || selectedDeviceIds.length === 0}
                                >
                                    Add {selectedDeviceIds.length} Device(s)
                                </Button>
                                <Button variant="outlined" onClick={() => setOpenAddDevices(false)}>
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>
                    </ModalDialog>
                </Modal>

                {/* Enhanced Assign Content to Group Modal */}
                <Modal open={openAssignContent} onClose={() => setOpenAssignContent(false)}>
                    <ModalDialog sx={{
                        minWidth: '95vw',
                        maxWidth: '95vw',
                        minHeight: '90vh',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        p: 3
                    }}>
                        <Typography level="h4" sx={{ mb: 2 }}>
                            📡 Assign Content to "{selectedGroup?.group_name}"
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
                            {submitError && (
                                <Alert color="danger" variant="soft">
                                    {submitError}
                                </Alert>
                            )}

                            {/* Settings Panel */}
                            <Card variant="outlined">
                                <CardContent>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid xs={12} md={3}>
                                            <FormControl required>
                                                <FormLabel>Assignment Type</FormLabel>
                                                <Select
                                                    value={contentForm.assignment_type}
                                                    onChange={(e, value) => setContentForm(prev => ({ ...prev, assignment_type: value }))}
                                                >
                                                    <Option value="layout">🎬 Layout Template</Option>
                                                    <Option value="content">📄 Individual Content</Option>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {contentForm.assignment_type === 'layout' && (
                                            <Grid xs={12} md={3}>
                                                <FormControl required>
                                                    <FormLabel>Layout Template ({layouts.length} available)</FormLabel>
                                                    <Select
                                                        value={contentForm.layout_id}
                                                        onChange={(e, value) => {
                                                            console.log('Layout selected:', value);
                                                            setContentForm(prev => ({ ...prev, layout_id: value }));
                                                        }}
                                                    >
                                                        <Option value="">Select Layout</Option>
                                                        {console.log('Rendering layout options, layouts array:', layouts)}
                                                        {layouts && layouts.length > 0 ? (
                                                            layouts.map(layout => {
                                                                console.log('Rendering layout option:', layout.id, layout.template_name);
                                                                return (
                                                                    <Option key={layout.id} value={layout.id}>
                                                                        🎬 {layout.template_name} (ID: {layout.id})
                                                                    </Option>
                                                                );
                                                            })
                                                        ) : (
                                                            <Option value="" disabled>Loading layouts...</Option>
                                                        )}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        )}


                                        <Grid xs={12} md={2}>
                                            <FormControl>
                                                <FormLabel>Priority</FormLabel>
                                                <Input
                                                    type="number"
                                                    value={contentForm.priority}
                                                    onChange={(e) => setContentForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 1 }))}
                                                    slotProps={{ input: { min: 1, max: 10 } }}
                                                />
                                            </FormControl>
                                        </Grid>

                                        <Grid xs={12} md={4}>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', height: '100%' }}>
                                                <Button
                                                    variant="solid"
                                                    color="success"
                                                    disabled={
                                                        (contentForm.assignment_type === 'layout' && !contentForm.layout_id) ||
                                                        (contentForm.assignment_type === 'content' && selectedContentIds.length === 0)
                                                    }
                                                    loading={submitting}
                                                    onClick={handleAssignContent}
                                                >
                                                    📡 Assign to {selectedGroup?.device_count || 0} Device(s)
                                                </Button>
                                                <Button variant="outlined" onClick={() => setOpenAssignContent(false)}>
                                                    Cancel
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Content Selection (only for individual content assignment) */}
                            {contentForm.assignment_type === 'content' && (
                                <Card variant="outlined" sx={{ flex: 1, overflow: 'hidden' }}>
                                    <Tabs value={activeContentTab} onChange={(e, value) => setActiveContentTab(value)}>
                                        <TabList>
                                            <Tab value="all">
                                                <Badge badgeContent={getContentStats().total} size="sm" color="primary">
                                                    All Content
                                                </Badge>
                                            </Tab>
                                            <Tab value="image">
                                                <Badge badgeContent={getContentStats().image} size="sm" color="primary">
                                                    🖼️ Images
                                                </Badge>
                                            </Tab>
                                            <Tab value="video">
                                                <Badge badgeContent={getContentStats().video} size="sm" color="primary">
                                                    🎥 Videos
                                                </Badge>
                                            </Tab>
                                            <Tab value="document">
                                                <Badge badgeContent={getContentStats().document} size="sm" color="primary">
                                                    📄 Documents
                                                </Badge>
                                            </Tab>
                                            <Tab value="web">
                                                <Badge badgeContent={getContentStats().web} size="sm" color="primary">
                                                    🌐 Web
                                                </Badge>
                                            </Tab>
                                        </TabList>

                                        <TabPanel value={activeContentTab} sx={{ p: 2, overflow: 'auto', height: '60vh' }}>
                                            <Grid container spacing={2}>
                                                {getFilteredContent().map((item) => {
                                                    const isSelected = selectedContentIds.includes(item.id);
                                                    const thumbnailUrl = buildThumbnailUrl(item) || getFallbackThumbnail();
                                                    const contentType = (item.type_name || '').toLowerCase();

                                                    return (
                                                        <Grid xs={6} sm={4} md={3} lg={2} key={item.id}>
                                                            <Card
                                                                variant={isSelected ? "solid" : "outlined"}
                                                                color={isSelected ? "primary" : "neutral"}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s',
                                                                    '&:hover': {
                                                                        transform: 'translateY(-2px)',
                                                                        boxShadow: 'sm'
                                                                    }
                                                                }}
                                                                onClick={() => toggleContentSelection(item.id)}
                                                            >
                                                                <AspectRatio ratio="16/9">
                                                                    <img
                                                                        src={thumbnailUrl}
                                                                        alt={item.content_name}
                                                                        style={{ objectFit: 'cover' }}
                                                                    />
                                                                </AspectRatio>
                                                                <CardContent sx={{ p: 1 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Checkbox
                                                                            checked={isSelected}
                                                                            onChange={() => toggleContentSelection(item.id)}
                                                                            size="sm"
                                                                        />
                                                                        <Typography level="body-sm" sx={{
                                                                            flex: 1,
                                                                            color: isSelected ? 'white' : 'inherit',
                                                                            fontWeight: isSelected ? 600 : 400
                                                                        }} noWrap>
                                                                            {contentType === 'image' && '🖼️'}
                                                                            {contentType === 'video' && '🎥'}
                                                                            {contentType === 'pdf' && '📄'}
                                                                            {contentType === 'web' && '🌐'}
                                                                            {!['image', 'video', 'pdf', 'web'].includes(contentType) && '📝'}
                                                                            {' '}{item.content_name}
                                                                        </Typography>
                                                                    </Box>
                                                                </CardContent>
                                                            </Card>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>

                                            {getFilteredContent().length === 0 && (
                                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography level="body-md" sx={{ color: 'neutral.500' }}>
                                                        No {activeContentTab === 'all' ? '' : activeContentTab} content available
                                                    </Typography>
                                                </Box>
                                            )}
                                        </TabPanel>
                                    </Tabs>
                                </Card>
                            )}

                            {/* Info Panel for Layout/Playlist Assignment */}
                            {contentForm.assignment_type !== 'content' && (
                                <Card variant="outlined" sx={{ flex: 1 }}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            {contentForm.assignment_type === 'layout' ? (
                                                <>
                                                    <Typography level="h3" sx={{ mb: 2 }}>🎬</Typography>
                                                    <Typography level="h4" sx={{ mb: 1 }}>Layout Template Assignment</Typography>
                                                    <Typography level="body-md" sx={{ color: 'neutral.600', mb: 2 }}>
                                                        Select a layout template from the dropdown above. The layout will be applied to all {selectedGroup?.device_count || 0} devices in this group.
                                                    </Typography>

                                                    {/* Debug info */}
                                                    <Alert color="primary" variant="soft" sx={{ mb: 2 }}>
                                                        <Typography level="body-sm">
                                                            Debug: layouts.length = {layouts.length}<br />
                                                            Force update counter: {forceUpdate}<br />
                                                            Available layouts: {JSON.stringify(layouts.map(l => ({ id: l.id, name: l.template_name })))}<br />
                                                            <Button size="sm" variant="outlined" onClick={() => {
                                                                console.log('Manual fetch triggered');
                                                                fetchContentOptions();
                                                            }}>
                                                                🔄 Reload Layouts
                                                            </Button>
                                                        </Typography>
                                                    </Alert>

                                                    {layouts.length === 0 ? (
                                                        <Alert color="warning" variant="soft">
                                                            No layout templates available. Create a layout first in the Layouts page.
                                                        </Alert>
                                                    ) : (
                                                        <Typography level="body-sm" sx={{ color: 'success.600' }}>
                                                            ✅ {layouts.length} layout template(s) available
                                                        </Typography>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <Typography level="h3" sx={{ mb: 2 }}>📄</Typography>
                                                    <Typography level="h4" sx={{ mb: 1 }}>Individual Content Assignment</Typography>
                                                    <Typography level="body-md" sx={{ color: 'neutral.600', mb: 2 }}>
                                                        Select individual content items from the thumbnail grid below. Multiple items can be assigned to all {selectedGroup?.device_count || 0} devices in this group.
                                                    </Typography>
                                                    {content.length === 0 ? (
                                                        <Alert color="warning" variant="soft">
                                                            No content available. Upload content first in the Content page.
                                                        </Alert>
                                                    ) : (
                                                        <Typography level="body-sm" sx={{ color: 'success.600' }}>
                                                            ✅ {content.length} content item(s) available
                                                        </Typography>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            )}
                        </Box>
                    </ModalDialog>
                </Modal>
            </Box>
        </MainLayout>
    );
};

export default DeviceGroupsPage;
